/**
 * WebSocket (STOMP) клиент для получения прогресса конвертации видео.
 * Бэкенд рассылает сообщения в /topic/video-conversion/{videoId}.
 * Payload: { videoId, percent, status: 'PROCESSING'|'READY'|'FAILED', message? }
 */
import { Client } from '@stomp/stompjs';

const API_BASE = process.env.VUE_APP_API_URL || '';
const WS_URL = API_BASE.replace(/^http/i, 'ws').replace(/\/$/, '') + '/websocket';

let client: Client | null = null;
const subscriptions = new Map<number, () => void>();
const callbacks = new Map<number, (data: { videoId: number; percent: number; status: string; message?: string }) => void>();

function getOrCreateClient(): Client {
  if (client) return client;
  client = new Client({
    brokerURL: WS_URL,
    reconnectDelay: 5000,
    debug: (str) => console.log('[STOMP]', str),
    onConnect: () => {
      const c = client;
      if (!c) return;
      subscriptions.forEach((_unsub, videoId) => {
        const sub = c.subscribe(`/topic/video-conversion/${videoId}`, (msg) => {
          try {
            if (!msg.body) return;
            const data = JSON.parse(msg.body);
            const cb = callbacks.get(videoId);
            if (cb) cb(data);
          } catch (err) {
            console.warn('Failed to parse conversion progress message:', err);
          }
        });
        subscriptions.set(videoId, () => sub.unsubscribe());
      });
    },
    onStompError: (frame) => {
      console.warn('Video conversion WebSocket STOMP error:', frame);
    },
  });
  client.activate();
  return client;
}

export function subscribeToConversionProgress(
  videoId: number,
  onProgress: (data: { videoId: number; percent: number; status: string; message?: string }) => void
): () => void {
  callbacks.set(videoId, onProgress);
  const c = getOrCreateClient();
  if (c.connected) {
    const sub = c.subscribe(`/topic/video-conversion/${videoId}`, (msg) => {
      try {
        if (!msg.body) return;
        const data = JSON.parse(msg.body);
        onProgress(data);
      } catch (err) {
        console.warn('Failed to parse conversion progress message:', err);
      }
    });
    subscriptions.set(videoId, () => sub.unsubscribe());
  } else {
    subscriptions.set(videoId, () => {});
  }
  return () => {
    const unsub = subscriptions.get(videoId);
    if (unsub) unsub();
    subscriptions.delete(videoId);
    callbacks.delete(videoId);
  };
}

export function disconnect(): void {
  if (client) {
    client.deactivate();
    client = null;
  }
  subscriptions.clear();
  callbacks.clear();
}
